import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import SplunkSvg from '~/assets/splunk.svg'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { Modal } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { UploadFile, type UploadState } from '~/components/common/UploadFile'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'

interface IntegrationItemModalProps {
  open: boolean
  onClose: () => void
  refreshIntegrations: () => void
  metadata: unknown
  isEditing: boolean
  onSetupComplete?: () => void
}

interface SplunkConnectionMetadata {
  base_url?: string
  allowed_indices?: string[]
}

interface SplunkTestConnectionResponse {
  success: boolean
  message: string
  indices?: string[]
}

interface ApiKeySummary {
  id: number
  truncated_token: string
  scope: string
  revoked: boolean
  created_at_unix: number
  last_used_at_unix: number | null
}

interface CreateApiKeyResponse {
  token: string
  expires_at_unix: number
}

const SPLUNK_WEBHOOK_SCOPE = 'monitoring_webhook:splunk'
const SPLUNK_WEBHOOK_PATH = '/api/monitoring/webhook/splunk'

export function SplunkItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing,
  onSetupComplete
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [baseUrl, setBaseUrl] = useState<string>('')
  const [apiKey, setApiKey] = useState<string>('')
  const [caCertPemContent, setCaCertPemContent] = useState<string>('')
  const [caCertUploadState, setCaCertUploadState] = useState<UploadState>({ status: 'default' })
  const [availableIndices, setAvailableIndices] = useState<string[]>([])
  const [selectedIndices, setSelectedIndices] = useState<string[]>([])
  const [isManageMode, setIsManageMode] = useState<boolean>(false)
  const [justCompletedSetup, setJustCompletedSetup] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isGeneratingWebhookKey, setIsGeneratingWebhookKey] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [webhookMessage, setWebhookMessage] = useState<string>('')
  const [fullWebhookUrl, setFullWebhookUrl] = useState<string | null>(null)
  const [maskedWebhookUrl, setMaskedWebhookUrl] = useState<string | null>(null)

  const webhookBaseUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return SPLUNK_WEBHOOK_PATH
    }
    return `${window.location.origin}${SPLUNK_WEBHOOK_PATH}`
  }, [])

  const buildWebhookUrl = useCallback(
    (tokenValue: string) => `${webhookBaseUrl}?token=${tokenValue}`,
    [webhookBaseUrl]
  )

  const getLatestSplunkWebhookKey = useCallback(
    (apiKeys: ApiKeySummary[]): ApiKeySummary | null => {
      const candidates = apiKeys
        .filter(key => key.scope === SPLUNK_WEBHOOK_SCOPE && !key.revoked)
        .sort((left, right) => right.created_at_unix - left.created_at_unix)
      return candidates[0] ?? null
    },
    []
  )

  const loadMaskedWebhookUrl = useCallback(async () => {
    if (!currentWorkspace?.uuid) {
      setMaskedWebhookUrl(null)
      return
    }

    try {
      const apiKeys = await fetchWrapper.get<ApiKeySummary[]>(
        `/api/workspace/${currentWorkspace.uuid}/api-key/`,
        { credentials: 'include' }
      )
      const latestKey = getLatestSplunkWebhookKey(apiKeys)
      if (latestKey?.truncated_token) {
        setMaskedWebhookUrl(buildWebhookUrl(latestKey.truncated_token))
      } else {
        setMaskedWebhookUrl(null)
      }
      setWebhookMessage('')
    } catch (error) {
      setMaskedWebhookUrl(null)
      const errorText = error instanceof Error ? error.message : 'Failed to load webhook URL.'
      setWebhookMessage(errorText)
    }
  }, [buildWebhookUrl, currentWorkspace?.uuid, getLatestSplunkWebhookKey])

  const generateWebhookUrl = useCallback(async (): Promise<string | null> => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return null
    }

    setIsGeneratingWebhookKey(true)
    setWebhookMessage('')
    try {
      const response = await fetchWrapper.post<CreateApiKeyResponse>(
        `/api/workspace/${currentWorkspace.uuid}/api-key/`,
        { scope: SPLUNK_WEBHOOK_SCOPE },
        { credentials: 'include' }
      )
      if (!response.token) {
        throw new Error('Missing API key token in response')
      }
      const webhookUrl = buildWebhookUrl(response.token)
      setFullWebhookUrl(webhookUrl)
      setMaskedWebhookUrl(null)
      return webhookUrl
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Failed to generate webhook URL.'
      setWebhookMessage(errorText)
      BetterToast.error(`Failed to generate webhook URL: ${errorText}`)
      return null
    } finally {
      setIsGeneratingWebhookKey(false)
    }
  }, [buildWebhookUrl, currentWorkspace?.uuid])

  const loadIndicesFromSavedConnection = useCallback(async () => {
    if (!currentWorkspace?.uuid) {
      setErrorMessage('Workspace not found')
      return
    }

    setIsTestingConnection(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<SplunkTestConnectionResponse>(
        `/api/workspace/${currentWorkspace.uuid}/integration/splunk_credentials/test`,
        {},
        { credentials: 'include' }
      )
      const fetchedIndices = Array.isArray(data.indices) ? data.indices : []
      const uniqueIndices = [...new Set(fetchedIndices)].sort((a, b) => a.localeCompare(b))
      setAvailableIndices(uniqueIndices)
      setSelectedIndices(prev => prev.filter(indexName => uniqueIndices.includes(indexName)))
      if (uniqueIndices.length === 0) {
        BetterToast.error('Connected, but no indices were returned for this API key.')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Failed to load available indices.'
      setErrorMessage(errorText)
    } finally {
      setIsTestingConnection(false)
    }
  }, [currentWorkspace?.uuid])

  const splunkMetadata = useMemo(() => metadata as SplunkConnectionMetadata | null, [metadata])

  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      return
    }

    if (wasOpenRef.current) {
      return
    }
    wasOpenRef.current = true

    const existingAllowedIndices = Array.isArray(splunkMetadata?.allowed_indices)
      ? [...new Set(splunkMetadata.allowed_indices)].sort((a, b) => a.localeCompare(b))
      : []

    setBaseUrl(splunkMetadata?.base_url || '')
    setApiKey('')
    setCaCertPemContent('')
    setCaCertUploadState({ status: 'default' })
    setAvailableIndices(existingAllowedIndices)
    setSelectedIndices(existingAllowedIndices)
    setIsManageMode(isEditing)
    setJustCompletedSetup(false)
    setErrorMessage('')
    setWebhookMessage('')
    setFullWebhookUrl(null)
    setMaskedWebhookUrl(null)

    if (isEditing && currentWorkspace?.uuid) {
      void loadIndicesFromSavedConnection()
      void loadMaskedWebhookUrl()
    }
  }, [
    open,
    isEditing,
    metadata,
    currentWorkspace?.uuid,
    loadIndicesFromSavedConnection,
    loadMaskedWebhookUrl
  ])

  const handleInputChange = (field: 'baseUrl' | 'apiKey', value: string) => {
    if (field === 'baseUrl') {
      setBaseUrl(value)
    } else {
      setApiKey(value)
    }

    if (errorMessage) {
      setErrorMessage('')
    }
  }

  const processCaCertFile = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase()
    const allowedExtensions = ['pem', 'crt', 'cer']
    if (!extension || !allowedExtensions.includes(extension)) {
      setCaCertUploadState({
        status: 'error',
        error: 'Please upload a PEM/CRT certificate file.',
        file: { name: file.name, size: file.size }
      })
      setCaCertPemContent('')
      return
    }

    setCaCertUploadState({
      status: 'uploading',
      file: { name: file.name, size: file.size }
    })

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const result = reader.result
        let certContent = ''
        if (typeof result === 'string') {
          certContent = result
        } else if (result instanceof ArrayBuffer) {
          certContent = new TextDecoder('utf-8').decode(result)
        } else {
          throw new Error('Unsupported certificate format')
        }

        setCaCertPemContent(certContent)
        setCaCertUploadState({
          status: 'uploaded',
          file: { name: file.name, size: file.size }
        })
      } catch (_error) {
        setCaCertPemContent('')
        setCaCertUploadState({
          status: 'error',
          error: 'Failed to process certificate file.',
          file: { name: file.name, size: file.size }
        })
      }
    }

    reader.onerror = () => {
      setCaCertPemContent('')
      setCaCertUploadState({
        status: 'error',
        error: 'Failed to read certificate file.',
        file: { name: file.name, size: file.size }
      })
    }

    reader.readAsArrayBuffer(file)
  }

  const handleCaCertSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const files = input.files
    if (!files || files.length === 0) {
      input.value = ''
      return
    }

    processCaCertFile(files[0])
    input.value = ''
  }

  const handleCaCertDrop = (file: File) => {
    processCaCertFile(file)
  }

  const handleCaCertRemove = () => {
    setCaCertPemContent('')
    setCaCertUploadState({ status: 'default' })
  }

  const handleToggleIndex = (indexName: string) => {
    setSelectedIndices(prev =>
      prev.includes(indexName)
        ? prev.filter(existingIndex => existingIndex !== indexName)
        : [...prev, indexName]
    )
  }

  const handleSaveConfiguration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const trimmedBaseUrl = baseUrl.trim()
      const trimmedApiKey = apiKey.trim()
      const trimmedCaCertPem = caCertPemContent.trim()

      if (!trimmedBaseUrl || !trimmedApiKey) {
        setErrorMessage('Please enter Base URL and API Key')
        return
      }

      const data = await fetchWrapper.post<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/splunk_credentials`,
        {
          base_url: trimmedBaseUrl,
          api_key: trimmedApiKey,
          allowed_indices: [],
          ca_cert_pem: trimmedCaCertPem || undefined
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success('Connection saved successfully.')
        setBaseUrl(trimmedBaseUrl)
        setIsManageMode(true)
        setJustCompletedSetup(true)
        setApiKey('')
        setCaCertPemContent('')
        setCaCertUploadState({ status: 'default' })
        refreshIntegrations()
        const generatedWebhookUrl = await generateWebhookUrl()
        if (generatedWebhookUrl) {
          BetterToast.success('Webhook URL generated. Copy it now.')
        }
      } else {
        BetterToast.error(data.message || 'Failed to save integration')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Please try again.'
      BetterToast.error(`Failed to save integration: ${errorText}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAllowedIndices = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const data = await fetchWrapper.put<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/splunk_credentials`,
        {
          allowed_indices: selectedIndices
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success('Allowed indices updated successfully!')
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || 'Failed to update allowed indices')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Please try again.'
      BetterToast.error(`Failed to update allowed indices: ${errorText}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteIntegration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    if (
      !window.confirm('Delete Splunk integration? This will remove the connection configuration.')
    ) {
      return
    }

    setIsDeleting(true)
    try {
      const data = await fetchWrapper.delete<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/splunk_credentials`,
        { credentials: 'include' }
      )
      if (data.success) {
        BetterToast.success('Integration deleted successfully!')
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || 'Failed to delete integration')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Please try again.'
      BetterToast.error(`Failed to delete integration: ${errorText}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRotateWebhookUrl = async () => {
    const generatedWebhookUrl = await generateWebhookUrl()
    if (!generatedWebhookUrl) {
      return
    }
    refreshIntegrations()
    BetterToast.success('Webhook URL rotated. Copy the new URL now.')
  }

  const handleCopyWebhookUrl = async () => {
    if (!fullWebhookUrl) {
      return
    }
    try {
      await navigator.clipboard.writeText(fullWebhookUrl)
      BetterToast.success('Webhook URL copied')
    } catch (_error) {
      BetterToast.error('Failed to copy webhook URL')
    }
  }

  const disabledSetupSave =
    isSaving ||
    !baseUrl.trim() ||
    !apiKey.trim() ||
    caCertUploadState.status === 'uploading' ||
    caCertUploadState.status === 'error'
  const disabledManageSave = isSaving || isDeleting || isTestingConnection || isGeneratingWebhookKey

  if (!isManageMode) {
    return (
      <Modal className='w-[640px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
        <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
          <img className='w-8 h-8' src={SplunkSvg} alt='Splunk' />
          <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold'>
            Splunk Setup
          </Typography>
        </div>
        <div className='p-12'>
          <div className='w-[440px] mx-auto flex flex-col gap-y-4'>
            <div className='flex flex-col gap-y-2'>
              <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                Base URL
              </Typography>
              <InputArea
                value={baseUrl}
                onChange={e => handleInputChange('baseUrl', e.target.value)}
                fullWidth
                singleLine
                placeholder='https://your-splunk-host:8089'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                API Key
              </Typography>
              <InputArea
                value={apiKey}
                onChange={e => handleInputChange('apiKey', e.target.value)}
                fullWidth
                singleLine
                type='password'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                CA Certificate PEM (Optional)
              </Typography>
              <UploadFile
                variant='without-toggle'
                uploadState={caCertUploadState}
                mainTitle='Upload PEM Certificate'
                description=''
                onFileSelect={handleCaCertSelect}
                onRemove={handleCaCertRemove}
                onErrorRemove={handleCaCertRemove}
                onDropFile={handleCaCertDrop}
                accept='.pem,.crt,.cer'
                inputId='splunkCaCertUpload'
                disabled={isSaving}
              />
              {caCertUploadState.error && (
                <Typography
                  fontFamily='geistMono'
                  fontSize='labelSm'
                  className='text-tokens-status-error-text'
                  fontWeight='semibold'
                >
                  {caCertUploadState.error}
                </Typography>
              )}
            </div>
            {errorMessage && (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text'
                fontWeight='semibold'
              >
                {errorMessage}
              </Typography>
            )}
          </div>
        </div>
        <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center'>
          <Button
            variant='primary'
            isDisabled={disabledSetupSave}
            onClick={handleSaveConfiguration}
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal className='w-[640px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img className='w-8 h-8' src={SplunkSvg} alt='Splunk' />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold'>
          Splunk Connection
        </Typography>
      </div>
      <div className='p-12'>
        <div className='w-[440px] mx-auto flex flex-col gap-y-4'>
          <div className='rounded-md border border-tokens-neutral-border-2 p-3 bg-tokens-neutral-bg-grey-1'>
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary'
            >
              Base URL
            </Typography>
            <Typography fontFamily='geistMono' fontSize='bodySm' className='break-all'>
              {baseUrl || 'Not available'}
            </Typography>
          </div>

          <div className='rounded-md border border-tokens-neutral-border-2 p-3 bg-tokens-neutral-bg-grey-1'>
            <div className='flex items-center justify-between gap-2'>
              <Typography
                fontFamily='geist'
                fontSize='labelSm'
                className='text-tokens-neutral-text-tertiary'
              >
                Webhook URL
              </Typography>
              <Button
                variant='outlined'
                onClick={handleRotateWebhookUrl}
                isDisabled={isGeneratingWebhookKey || isSaving || isDeleting}
              >
                {isGeneratingWebhookKey
                  ? 'Generating...'
                  : fullWebhookUrl || maskedWebhookUrl
                    ? 'Rotate'
                    : 'Generate'}
              </Button>
            </div>
            <Typography fontFamily='geistMono' fontSize='bodySm' className='break-all mt-2'>
              {fullWebhookUrl || maskedWebhookUrl || 'No webhook URL generated yet.'}
            </Typography>
            <div className='flex items-center justify-between mt-2'>
              <Typography
                fontFamily='geist'
                fontSize='labelSm'
                className='text-tokens-neutral-text-tertiary'
              >
                {fullWebhookUrl
                  ? 'Shown once. Copy it now.'
                  : 'Token is masked after this modal is closed.'}
              </Typography>
              {fullWebhookUrl ? (
                <Button variant='borderless' onClick={handleCopyWebhookUrl}>
                  Copy URL
                </Button>
              ) : null}
            </div>
            {webhookMessage ? (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text mt-2'
                fontWeight='semibold'
              >
                {webhookMessage}
              </Typography>
            ) : null}
          </div>

          {errorMessage && (
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              className='text-tokens-status-error-text'
              fontWeight='semibold'
            >
              {errorMessage}
            </Typography>
          )}
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-between items-center'>
        <Button
          variant='destructive'
          onClick={handleDeleteIntegration}
          isDisabled={isDeleting || isSaving}
        >
          {isDeleting ? 'Deleting...' : 'Delete Integration'}
        </Button>
        {justCompletedSetup && onSetupComplete ? (
          <Button variant='primary' onClick={onSetupComplete} isDisabled={disabledManageSave}>
            Continue
          </Button>
        ) : (
          <Button variant='primary' onClick={onClose} isDisabled={disabledManageSave}>
            Done
          </Button>
        )}
      </div>
    </Modal>
  )
}
